import { useContext } from 'react';
import { config } from '../config';
import { Question, responseValidateQuestion } from '../interfaces';
import { TriviaContext } from '../context';

interface useTriviaResponse {
  questions: Question[];
  activeQuestion: Question | null;
  isLoadTrivia: boolean;
  missingResponse: boolean;
  result: responseValidateQuestion[] | null;
  isOpenTrivia: boolean;
  triviaId: number | null;
  attempt: number;

  // tiempo
  timeElapsed: number;

  getTrivia: () => Promise<void>;
  validateQuestion: (idQuestion: string, idOptionSelected: string) => responseValidateQuestion;
  changeQuestion: (question: Question) => void;
  selectResponseQuestion: (question: Question, optionId: string) => void;
  validateTrivia: () => void;
  openModalTrivia: () => void;
  onCloseModalTrivia: () => void;
  setCurrentTriviaId: (id: number) => void;
  retryTrivia: () => Promise<void>
}

export const useTrivia = (): useTriviaResponse => {
  const { api_html5 } = config;

  const {
    // state
    questions,
    result,
    activeQuestion,
    missingResponse,
    isLoadTrivia,
    isOpenTrivia,
    triviaId,

    // timer (DEL PROVIDER)
    timeElapsed,
    setTimeElapsed,
    setTimerRunning,

    // actions
    setTrivia,
    setCurrentQuestion,
    selectQuestion,
    setMissingResponse,
    saveResponseTrivia,
    setLoadTrivia,
    onOpenTrivia,
    onCloseAndClearTrivia,
    setTriviaId,
    attempt,
    incrementAttempt,
    resetAttempt,
    retryTriviaState
  } = useContext(TriviaContext);

  /**
   * Obtener trivia e iniciar cronómetro
   */
  const getTrivia = async () => {
    try {
      setLoadTrivia();

      setTimeElapsed(0);        // REINICIA TIEMPO
      setTimerRunning(true);    // ARRANCA CRONÓMETRO

      // iniciar timer
      setTimeElapsed(0);
      setTimerRunning(true);

      const res = await fetch(
        `https://apivirtualplant.com/apiVpro/public/api/trivia_sr/${triviaId}`
      );
      const data: Question[] = await res.json();

      if (!data) {
        throw new Error('[ERROR] No se pudo obtener la trivia correctamente');
      }

      setTrivia(data);
      setCurrentQuestion(data[0]);
    } catch (error) {
      console.log('[ERROR][getTrivia]', error);
    }
  };

  const retryTrivia = async () => {
    setTimerRunning(false);
    setTimeElapsed(0);

    retryTriviaState();
    incrementAttempt();       // nuevo intento

    await getTrivia();
  };

  /**
   * Validar una pregunta
   */
  const validateQuestion = (idQuestion: string): responseValidateQuestion => {
    const question = questions.find(q => q.id === idQuestion);
    if (!question) throw new Error('Pregunta no encontrada');

    const optionSelect = question.opciones.find(
      o => question.respuesta === o.id
    )!;
    const optionCorrect = question.opciones.find(
      o => question.idOK === o.id
    )!;

    return {
      ok: question.idOK === question.respuesta,
      msg: question.idOK === question.respuesta ? 'Correcto!' : 'Incorrecto!',
      optionCorrect,
      optionSelect,
      question
    };
  };

  /**
   * Validar trivia completa y detener cronómetro
   */
  const validateTrivia = () => {
    const questionsResponse: responseValidateQuestion[] = [];
    setMissingResponse(false);

    for (const question of questions) {
      if (question.respuesta.length <= 0) {
        changeQuestion(question);
        setMissingResponse(true);
        return;
      }

      questionsResponse.push(validateQuestion(question.id));
    }

    // detener timer
    setTimerRunning(false);

    saveResponseTrivia(questionsResponse);
  };

  const changeQuestion = (question: Question) => {
    setCurrentQuestion(question);
  };

  const selectResponseQuestion = (question: Question, optionId: string) => {
    selectQuestion(question, optionId);
  };

  const openModalTrivia = () => {
    onOpenTrivia();
  };

  const onCloseModalTrivia = () => {
    // limpiar timer
    setTimerRunning(false);
    setTimeElapsed(0);
    resetAttempt();
    onCloseAndClearTrivia();
  };

  const setCurrentTriviaId = (id: number) => {
    setTriviaId(id);
  };

  return {
    questions,
    isOpenTrivia,
    result,
    activeQuestion,
    missingResponse,
    isLoadTrivia,
    triviaId,

    // tiempo expuesto
    timeElapsed,

    getTrivia,
    validateQuestion,
    changeQuestion,
    selectResponseQuestion,
    validateTrivia,
    openModalTrivia,
    onCloseModalTrivia,
    setCurrentTriviaId,
    attempt,
    retryTrivia
  };
};
